import { useCallback, useEffect } from "react";
import { useUseCaseManager } from "@curio/client/react";
import { useNotification } from "@context/NotificationProvider";
import { isAuthError } from "@/lib/curio";
import { OpenOptions } from "@curio/client/react/types";
import { logCurrentStateTriggers, UseCaseTriggersFromCurrentState } from "@utils/useCaseTriggersLogger";

interface UseUseCaseControlsOptions {
  enableLogs?: boolean;
}

export const useUseCaseControls = (options: UseUseCaseControlsOptions = {}) => {
  const { enableLogs = false } = options;
  const { open, close, status, error, triggersFromCurrentState } = useUseCaseManager();
  const { setNotification } = useNotification();

  const openSafe = useCallback(
    async (openOptions?: OpenOptions | undefined) => {
      try {
        await open(openOptions);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Erro ao abrir o caso de uso";
        setNotification({
          message,
          type: "error",
          isAuthError: isAuthError(err)
        });
      }
    },
    [open, setNotification]
  );

  const logCurrentState = useCallback(async () => {
    const result = (await triggersFromCurrentState()) as UseCaseTriggersFromCurrentState;
    logCurrentStateTriggers(result);
  }, [triggersFromCurrentState]);

  useEffect(() => {
    if (enableLogs && import.meta.env.DEV && status === "open") {
      logCurrentState();
    }
  }, [enableLogs, status, logCurrentState]);

  // `isReady` centraliza o padrão já usado (isolado) em MovimentarDocumentosPage.tsx -- telas com
  // UseCaseManager não devem habilitar ações antes da UC terminar de abrir. Gatear só no `isPending`
  // de uma mutation específica deixa uma janela real entre o mount e essa mutation ser disparada (ver
  // harness/29-.../state/progress.md, Fases 18/19/20/23 -- mesmo formato de bug encontrado 3x em
  // telas escritas independentemente, incluindo um caso onde o clique em si se perdeu, não só uma
  // lista vazia).
  const isReady = status === "open";

  return { open: openSafe, close, status, error, isReady, logCurrentStateTriggers: logCurrentState };
};
